import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

const AnalogTimePicker = ({ selectedDate, onChange, minTime, maxTime, disabledIntervals = [] }) => {
  const [mode, setMode] = useState('hours'); // 'hours' or 'minutes'
  const [tempDate, setTempDate] = useState(selectedDate || new Date());
  const [hourInput, setHourInput] = useState(moment(selectedDate || new Date()).format('hh'));
  const [minuteInput, setMinuteInput] = useState(moment(selectedDate || new Date()).format('mm'));
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (selectedDate) {
      setTempDate(selectedDate);
      setHourInput(moment(selectedDate).format('hh'));
      setMinuteInput(moment(selectedDate).format('mm'));
    }
  }, [selectedDate]);

  const hours = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
  const minutes = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];

  const handleHourChange = (e) => {
    setHourInput(e.target.value);
  };

  const handleHourBlur = () => {
    let val = parseInt(hourInput, 10);
    if (isNaN(val) || val < 1 || val > 12) {
      setHourInput(moment(tempDate).format('hh'));
      return;
    }

    const currentHours = tempDate.getHours();
    const isPM = currentHours >= 12;
    let newHour = val;
    if (val === 12) {
      newHour = isPM ? 12 : 0;
    } else {
      newHour = isPM ? val + 12 : val;
    }

    const newDate = new Date(tempDate);
    newDate.setHours(newHour);

    // Use 'minute' precision to avoid issues with seconds
    if (minTime && moment(newDate).isBefore(moment(minTime), 'minute')) {
      setHourInput(moment(tempDate).format('hh'));
      return;
    }

    setTempDate(newDate);
    onChange(newDate);
    setHourInput(moment(newDate).format('hh'));
  };

  const handleMinuteChange = (e) => {
    setMinuteInput(e.target.value);
  };

  const handleMinuteBlur = () => {
    let val = parseInt(minuteInput, 10);
    if (isNaN(val) || val < 0 || val > 59) {
      setMinuteInput(moment(tempDate).format('mm'));
      return;
    }

    const newDate = new Date(tempDate);
    newDate.setMinutes(val);

    // Use 'minute' precision to avoid issues with seconds
    if (minTime && moment(newDate).isBefore(moment(minTime), 'minute')) {
      setMinuteInput(moment(tempDate).format('mm'));
      return;
    }

    setTempDate(newDate);
    onChange(newDate);
    setMinuteInput(moment(newDate).format('mm'));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.target.blur();
    }
  };

  const handleHourClick = (hour, autoSwitchMode = true) => {
    const newDate = new Date(tempDate);
    // Handle 12 AM/PM logic if needed, but for now let's assume 24h or handle AM/PM separately
    // Let's stick to 24h or 12h with AM/PM toggle?
    // The design usually has AM/PM toggle.
    
    // Simple logic: preserve current AM/PM
    const currentHours = newDate.getHours();
    const isPM = currentHours >= 12;
    
    let newHour = hour;
    if (hour === 12) {
      newHour = isPM ? 12 : 0;
    } else {
      newHour = isPM ? hour + 12 : hour;
    }
    
    newDate.setHours(newHour);
    
    // Validate min/max
    if (minTime && moment(newDate).isBefore(moment(minTime))) {
        // If invalid, maybe don't select? Or select nearest?
        // For now, just allow and let validation handle it or disable the button
    }
    
    setTempDate(newDate);
    onChange(newDate);
    setHourInput(moment(newDate).format('hh'));

    if (autoSwitchMode) {
      setMode('minutes');
    }
  };

  const handleMinuteClick = (minute) => {
    const newDate = new Date(tempDate);
    newDate.setMinutes(minute);
    setTempDate(newDate);
    onChange(newDate);
    // Optionally switch back to hours or stay? Usually close or stay.
  };

  const toggleAmPm = () => {
    const newDate = new Date(tempDate);
    const currentHours = newDate.getHours();
    if (currentHours >= 12) {
      newDate.setHours(currentHours - 12);
    } else {
      newDate.setHours(currentHours + 12);
    }
    
    if (minTime && moment(newDate).isBefore(moment(minTime))) {
        return;
    }

    setTempDate(newDate);
    onChange(newDate);
  };

  const isTimeDisabled = (checkDate) => {
    if (minTime && moment(checkDate).isBefore(moment(minTime))) return true;
    if (maxTime && moment(checkDate).isAfter(moment(maxTime))) return true;
    if (disabledIntervals && disabledIntervals.length) {
      const t = +checkDate;
      for (const iv of disabledIntervals) {
        if (!iv || !iv.start || !iv.end) continue;
        const s = +(iv.start instanceof Date ? iv.start : new Date(iv.start));
        const e = +(iv.end instanceof Date ? iv.end : new Date(iv.end));
        if (!isNaN(s) && !isNaN(e) && t >= s && t <= e) return true;
      }
    }
    return false;
  };

  const renderClockFace = () => {
    const items = mode === 'hours' ? hours : minutes;
    const radius = 75;
    const center = 85; // slightly larger than radius
    
    const calculateTimeFromEvent = (e, autoSwitchMode = false) => {
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      
      const dx = x - center;
      const dy = y - center;
      
      let angle = Math.atan2(dy, dx) * 180 / Math.PI;
      // Adjust so 12 o'clock is 0 degrees (pointing up)
      angle += 90;
      if (angle < 0) angle += 360;
      
      if (mode === 'hours') {
        let hour = Math.round(angle / 30);
        if (hour === 0) hour = 12;
        
        // Check if disabled
        if (minTime || (disabledIntervals && disabledIntervals.length)) {
             const checkDate = new Date(tempDate);
             const currentHours = checkDate.getHours();
             const isPM = currentHours >= 12;
             let hourToCheck = hour;
             if (hour === 12) {
               hourToCheck = isPM ? 12 : 0;
             } else {
               hourToCheck = isPM ? hour + 12 : hour;
             }
             checkDate.setHours(hourToCheck);
             if (isTimeDisabled(checkDate)) return;
        }
        
        handleHourClick(hour, autoSwitchMode);
      } else {
        let minute = Math.round(angle / 6);
        if (minute === 60) minute = 0;
        
        // Check if disabled
        if (minTime || (disabledIntervals && disabledIntervals.length)) {
            const checkDate = new Date(tempDate);
            checkDate.setMinutes(minute);
          if (isTimeDisabled(checkDate)) return;
        }
        
        handleMinuteClick(minute);
      }
    };

    const handleMouseDown = (e) => {
      setIsDragging(true);
      calculateTimeFromEvent(e, false);
    };

    const handleMouseMove = (e) => {
      if (isDragging) {
        calculateTimeFromEvent(e, false);
      }
    };

    const handleMouseUp = (e) => {
      if (isDragging) {
        calculateTimeFromEvent(e, true);
      }
      setIsDragging(false);
    };

    const getHandAngle = () => {
        if (mode === 'hours') {
            const h = tempDate.getHours();
            return (h % 12) * 30;
        } else {
            const m = tempDate.getMinutes();
            return m * 6;
        }
    };

    return (
      <div 
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        style={{ 
            position: 'relative', 
            width: '170px', 
            height: '170px', 
            margin: '0 auto', 
            background: '#f8f9fa', 
            borderRadius: '50%', 
            boxShadow: 'inset 0 0 10px rgba(0,0,0,0.1)',
            cursor: 'pointer'
        }}
      >
        {/* Clock Hand */}
        <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: '2px',
            height: '40%',
            background: '#65badb',
            transformOrigin: 'bottom center',
            transform: `translate(-50%, -100%) rotate(${getHandAngle()}deg)`,
            pointerEvents: 'none',
            zIndex: 1
        }}>
            {/* Dot at the end of the hand */}
            <div style={{
                position: 'absolute',
                top: '0',
                left: '50%',
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                background: '#65badb',
                transform: 'translate(-50%, -50%)'
            }}></div>
        </div>

        {/* Center dot */}
        <div style={{ position: 'absolute', top: '50%', left: '50%', width: '6px', height: '6px', background: '#65badb', borderRadius: '50%', transform: 'translate(-50%, -50%)', zIndex: 2 }}></div>
        
        {items.map((item, index) => {
          // Calculate position
          // 12 is at -90 degrees (top)
          // Angle per item: 360 / 12 = 30 degrees
          // 12 -> -90, 1 -> -60, ...
          const angleDeg = (index * 30) - 90; 
          const angleRad = angleDeg * (Math.PI / 180);
          const x = center + radius * 0.8 * Math.cos(angleRad);
          const y = center + radius * 0.8 * Math.sin(angleRad);

          let isSelected = false;
          if (mode === 'hours') {
            const h = tempDate.getHours();
            const displayH = h % 12 || 12;
            isSelected = displayH === item;
          } else {
            const m = tempDate.getMinutes();
            isSelected = m === item;
          }

          // Check if disabled
          let isDisabled = false;
          if (minTime || (disabledIntervals && disabledIntervals.length)) {
            const checkDate = new Date(tempDate);
            if (mode === 'hours') {
              // Check if this hour is valid.
              // We need to construct a date with this hour and check if it's before minTime.
              // But we need to respect the current AM/PM setting.
              const currentHours = checkDate.getHours();
              const isPM = currentHours >= 12;
              let hourToCheck = item;
              if (item === 12) {
                hourToCheck = isPM ? 12 : 0;
              } else {
                hourToCheck = isPM ? item + 12 : item;
              }
              checkDate.setHours(hourToCheck);
              
              // Let's compare hours directly if on same day.
              if (isTimeDisabled(checkDate)) isDisabled = true;
            } else {
              // Minutes
              checkDate.setMinutes(item);
              if (isTimeDisabled(checkDate)) isDisabled = true;
            }
          }

          return (
            <button
              key={item}
              type="button"
              disabled={isDisabled}
              style={{
                position: 'absolute',
                left: `${x}px`,
                top: `${y}px`,
                transform: 'translate(-50%, -50%)',
                width: '28px',
                height: '28px',
                borderRadius: '50%',
                border: 'none',
                background: isSelected ? '#65badb' : 'transparent',
                color: isDisabled ? '#dee2e6' : (isSelected ? '#fff' : '#495057'),
                fontWeight: isSelected ? 'bold' : 'normal',
                cursor: isDisabled ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
                transition: 'all 0.2s',
                pointerEvents: isDisabled ? 'none' : 'auto',
                zIndex: 2
              }}
            >
              {item === 0 && mode === 'minutes' ? '00' : item}
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <div className="analog-time-picker" style={{ padding: '0', textAlign: 'center' }}>
      <div className="time-display" style={{ marginBottom: '10px', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '5px' }}>
        <input
            type="text"
            value={hourInput}
            onChange={handleHourChange}
            onBlur={handleHourBlur}
            onKeyDown={handleKeyDown}
            onClick={() => setMode('hours')}
            style={{ 
                fontSize: '32px', 
                fontWeight: 'bold', 
                color: mode === 'hours' ? '#65badb' : '#adb5bd',
                cursor: 'pointer',
                width: '50px',
                border: 'none',
                background: 'transparent',
                textAlign: 'center',
                outline: 'none'
            }}
        />
        <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#adb5bd' }}>:</div>
        <input
            type="text"
            value={minuteInput}
            onChange={handleMinuteChange}
            onBlur={handleMinuteBlur}
            onKeyDown={handleKeyDown}
            onClick={() => setMode('minutes')}
            style={{ 
                fontSize: '32px', 
                fontWeight: 'bold', 
                color: mode === 'minutes' ? '#65badb' : '#adb5bd',
                cursor: 'pointer',
                width: '50px',
                border: 'none',
                background: 'transparent',
                textAlign: 'center',
                outline: 'none'
            }}
        />
        <div style={{ marginLeft: '8px', display: 'flex', flexDirection: 'column' }}>
            <button 
                type="button"
                onClick={toggleAmPm}
                style={{ 
                    border: 'none', 
                    background: 'transparent', 
                    color: tempDate.getHours() >= 12 ? '#adb5bd' : '#65badb',
                    fontWeight: 'bold',
                    fontSize: '16px',
                    cursor: 'pointer',
                    padding: '0'
                }}
            >
                AM
            </button>
            <button 
                type="button"
                onClick={toggleAmPm}
                style={{ 
                    border: 'none', 
                    background: 'transparent', 
                    color: tempDate.getHours() >= 12 ? '#65badb' : '#adb5bd',
                    fontWeight: 'bold',
                    fontSize: '16px',
                    cursor: 'pointer',
                    padding: '0'
                }}
            >
                PM
            </button>
        </div>
      </div>

      {renderClockFace()}
      
      <div style={{ marginTop: '5px', fontSize: '11px', color: '#6c757d' }}>
        Select {mode === 'hours' ? 'Hour' : 'Minute'}
      </div>
    </div>
  );
};

AnalogTimePicker.propTypes = {
  selectedDate: PropTypes.instanceOf(Date),
  onChange: PropTypes.func.isRequired,
  minTime: PropTypes.instanceOf(Date),
  maxTime: PropTypes.instanceOf(Date),
};

export default AnalogTimePicker;
